import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api';

interface ServerInfo {
  version: string;
  name: string;
  features: {
    // Backend-agnostic analytics signal (true for clickhouse OR postgres
    // backend). Gate the Analytics tab on this, not on `clickhouse`.
    // Optional: an older backend won't emit it — consumers fall back to
    // `clickhouse` (see device-detail's analyticsEnabled).
    analytics?: boolean;
    clickhouse: boolean;
    s3: boolean;
    mqtt: boolean;
    // MCP / AI Access page + /mcp server (off unless APP_MCP_ENABLED=true).
    mcp?: boolean;
  };
  capture?: {
    wait_for_ack: boolean;
  };
}

const DEFAULT_FEATURES: ServerInfo['features'] = {
  analytics: false,
  clickhouse: false,
  s3: false,
  mqtt: false,
};

// Module-level guard so the clickhouse-deprecation warning fires at most once
// per page load (not per render). Only relevant in the deploy-skew window
// where a new frontend talks to an older backend that lacks features.analytics.
let clickhouseDeprecationWarned = false;

// Default to async: snappier click UX, matches the backend default.
const DEFAULT_CAPTURE: NonNullable<ServerInfo['capture']> = {
  wait_for_ack: false,
};

export function useServerInfo() {
  return useQuery({
    queryKey: ['server-info'],
    queryFn: () => apiClient.get<ServerInfo>('/info'),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useFeatures(): ServerInfo['features'] {
  const { data } = useServerInfo();
  if (
    !clickhouseDeprecationWarned &&
    data?.features &&
    data.features.analytics === undefined &&
    data.features.clickhouse
  ) {
     
    console.warn('features.clickhouse is deprecated; upgrade the backend to emit features.analytics');
    clickhouseDeprecationWarned = true;
  }
  return data?.features ?? DEFAULT_FEATURES;
}

// Separate hook so consumers don't re-render on features changes.
export function useCaptureFlags(): NonNullable<ServerInfo['capture']> {
  const { data } = useServerInfo();
  return data?.capture ?? DEFAULT_CAPTURE;
}
